import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import { api, API, BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Upload, User, Building2, Mail, CheckCircle2, XCircle, Send } from "lucide-react";
import { toast } from "sonner";

function HslPicker({ label, value, onChange, testid }) {
  const [h, s, l] = value.split(" ").map(v => parseInt(v));
  const update = (nh, ns, nl) => onChange(`${nh} ${ns}% ${nl}%`);
  return (
    <div className="space-y-2">
      <Label className="label-caps">{label}</Label>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded border border-border" style={{background: `hsl(${value})`}} data-testid={`${testid}-swatch`} />
        <div className="flex-1 grid grid-cols-3 gap-2">
          <Input data-testid={`${testid}-h`} type="number" value={h} onChange={e => update(e.target.value, s, l)} />
          <Input data-testid={`${testid}-s`} type="number" value={s} onChange={e => update(h, e.target.value, l)} />
          <Input data-testid={`${testid}-l`} type="number" value={l} onChange={e => update(h, s, e.target.value)} />
        </div>
      </div>
      <div className="text-[10px] font-mono text-muted-foreground">H S L (0-360, 0-100, 0-100)</div>
    </div>
  );
}

export default function Admin() {
  const { branding, update } = useBranding();
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [cases, setCases] = useState([]);
  const [videoDrafts, setVideoDrafts] = useState({});
  const [profile, setProfile] = useState({ display_name: "", signature: "", profile_photo_path: "", business_logo_path: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [newInstructor, setNewInstructor] = useState({ name: "", email: "", password: "", display_name: "" });
  const [creating, setCreating] = useState(false);
  const [chapterVideos, setChapterVideos] = useState([]);
  const [uploadingChapter, setUploadingChapter] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [trialStats, setTrialStats] = useState(null);
  const [welcome, setWelcome] = useState({ video_path: null, video_url: null });
  const [uploadingWelcome, setUploadingWelcome] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [faqDraft, setFaqDraft] = useState({ question: "", answer: "" });
  const [faqSaving, setFaqSaving] = useState(false);

  const loadInstructors = () => api.get("/admin/instructors").then(r => setInstructors(r.data)).catch(() => {});
  const loadChapterVideos = () => api.get("/admin/chapter-videos").then(r => setChapterVideos(r.data)).catch(() => {});
  const loadEmailStatus = () => api.get("/admin/email/status").then(r => setEmailStatus(r.data)).catch(() => {});
  const loadTrialStats = () => api.get("/admin/trial-stats").then(r => setTrialStats(r.data)).catch(() => {});
  const loadWelcome = () => api.get("/instructor/welcome-video/mine").then(r => setWelcome(r.data)).catch(() => {});
  const loadFaqs = () => api.get("/admin/assistant/faqs").then(r => setFaqs(r.data)).catch(() => {});

  useEffect(() => { setForm(branding); }, [branding]);
  useEffect(() => {
    api.get("/practice").then(r => {
      setCases(r.data);
      const d = {};
      r.data.forEach(c => { d[c.id] = c.video_url || ""; });
      setVideoDrafts(d);
    });
    api.get("/instructor/me").then(r => setProfile(r.data)).catch(() => {});
    loadInstructors();
    loadChapterVideos();
    loadEmailStatus();
    loadTrialStats();
    loadWelcome();
    loadFaqs();
    if (currentUser?.email) setTestEmailTo(currentUser.email);
  }, [currentUser?.email]);

  const save = async () => {
    setSaving(true);
    try {
      await update(form);
      toast.success("Branding saved and applied.");
    } catch (e) { toast.error("Save failed. Admin permission required."); }
    finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put("/instructor/profile", { display_name: profile.display_name, signature: profile.signature });
      toast.success("Instructor profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setProfileSaving(false); }
  };

  const createFaq = async () => {
    const q = faqDraft.question.trim(), a = faqDraft.answer.trim();
    if (!q || !a) { toast.error("Both question and answer are required."); return; }
    setFaqSaving(true);
    try {
      await api.post("/admin/assistant/faqs", { question: q, answer: a });
      setFaqDraft({ question: "", answer: "" });
      await loadFaqs();
      toast.success("FAQ added. Your AI helper will quote this verbatim next time it's asked.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that FAQ."); }
    finally { setFaqSaving(false); }
  };

  const updateFaq = async (id, patch) => {
    try {
      await api.put(`/admin/assistant/faqs/${id}`, patch);
      await loadFaqs();
      toast.success("FAQ updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm("Delete this FAQ? The AI helper will stop using its answer.")) return;
    try {
      await api.delete(`/admin/assistant/faqs/${id}`);
      await loadFaqs();
      toast.success("FAQ deleted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (5 MB max)"); return; }
    setUploading(kind);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post(`/instructor/upload?kind=${kind}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(p => ({ ...p, [kind === "profile_photo" ? "profile_photo_path" : "business_logo_path"]: r.data.path }));
      toast.success(kind === "profile_photo" ? "Profile photo uploaded" : "Business logo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(null); }
  };

  const saveVideo = async (case_id) => {
    try {
      await api.put("/practice/video", { case_id, video_url: videoDrafts[case_id] || "" });
      toast.success("Video URL saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const createInstructor = async () => {
    if (!newInstructor.name.trim() || !newInstructor.email.trim() || newInstructor.password.length < 8) {
      toast.error("Fill name, email, and a password of 8+ characters");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/instructors", {
        name: newInstructor.name.trim(),
        email: newInstructor.email.trim().toLowerCase(),
        password: newInstructor.password,
        display_name: newInstructor.display_name.trim() || undefined,
      });
      toast.success(`Instructor ${newInstructor.name} added`);
      setNewInstructor({ name: "", email: "", password: "", display_name: "" });
      loadInstructors();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setCreating(false); }
  };

  const removeInstructor = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their answered questions and profile stay in the record.`)) return;
    try {
      await api.delete(`/admin/instructors/${id}`);
      toast.success("Instructor removed");
      loadInstructors();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const uploadChapterVideo = async (chapter_id, file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Video too large (200 MB max)"); return; }
    setUploadingChapter(chapter_id);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/chapter-videos/${chapter_id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Chapter ${chapter_id} intro video uploaded`);
      loadChapterVideos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingChapter(null); }
  };

  const removeChapterVideo = async (chapter_id) => {
    if (!window.confirm(`Remove chapter ${chapter_id} intro video?`)) return;
    try {
      await api.delete(`/admin/chapter-videos/${chapter_id}`);
      toast.success("Video removed");
      loadChapterVideos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const sendTestEmail = async () => {
    if (!testEmailTo) { toast.error("Enter an email to test with"); return; }
    setSendingTest(true);
    try {
      const r = await api.post("/admin/email/test", { to: testEmailTo });
      toast.success(`Test email sent to ${r.data.to} — check the inbox`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingTest(false); }
  };

  const uploadWelcomeVideo = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("Video too large (200 MB max)"); return; }
    setUploadingWelcome(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/instructor/welcome-video", form, { headers: { "Content-Type": "multipart/form-data" } });
      setWelcome(r.data);
      toast.success("Welcome video uploaded — new students will see it on first login");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingWelcome(false); }
  };

  const removeWelcomeVideo = async () => {
    if (!window.confirm("Remove your welcome video?")) return;
    try {
      await api.delete("/instructor/welcome-video");
      setWelcome({ video_path: null, video_url: null });
      toast.success("Welcome video removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3">White-label admin</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-10">Branding.</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card className="p-6 space-y-5">
            <div>
              <div className="label-caps mb-3">Your tax company</div>
              <p className="text-xs text-muted-foreground mb-4">These appear on the certificate, sidebar, and student-facing pages so learners see who's teaching them.</p>
            </div>
            <div>
              <Label className="label-caps mb-2 block">Company / brand name</Label>
              <Input data-testid="brand-name-input" value={form.brand_name || ""} onChange={e => setForm({...form, brand_name: e.target.value})} />
            </div>
            <div>
              <Label className="label-caps mb-2 block">Tagline</Label>
              <Input data-testid="brand-tagline-input" value={form.tagline || ""} onChange={e => setForm({...form, tagline: e.target.value})} placeholder="Professional tax preparation & training" />
            </div>
            <div>
              <Label className="label-caps mb-2 block">Logo URL</Label>
              <Input data-testid="logo-url-input" value={form.logo_url || ""} onChange={e => setForm({...form, logo_url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-caps mb-2 block">Website</Label>
                <Input data-testid="brand-website-input" value={form.website || ""} onChange={e => setForm({...form, website: e.target.value})} placeholder="dynamicfinancials.com" />
              </div>
              <div>
                <Label className="label-caps mb-2 block">Phone</Label>
                <Input data-testid="brand-phone-input" value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-caps mb-2 block">Business email</Label>
                <Input data-testid="brand-email-input" value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} placeholder="hello@dynamicfinancials.com" />
              </div>
              <div>
                <Label className="label-caps mb-2 block">Facebook</Label>
                <Input data-testid="brand-facebook-input" value={form.facebook || ""} onChange={e => setForm({...form, facebook: e.target.value})} placeholder="https://facebook.com/DynamicFinancials" />
              </div>
            </div>
            <div>
              <Label className="label-caps mb-2 block">Business address</Label>
              <Input data-testid="brand-address-input" value={form.address || ""} onChange={e => setForm({...form, address: e.target.value})} placeholder="123 Main St, Anywhere, USA" />
            </div>

            <div className="border-t border-border pt-4 mt-4">
              <div className="label-caps text-[10px] text-primary mb-2">Live chat · Tawk.to</div>
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Free live chat that replies from your phone. Sign up at <a href="https://tawk.to" target="_blank" rel="noopener noreferrer" className="underline text-primary">tawk.to</a> (60 seconds, no card), then go to Admin → Chat Widgets and copy the two IDs from the widget URL: <code className="text-[10px] bg-secondary/50 px-1 rounded">tawk.to/embed/<strong>PROPERTY</strong>/<strong>WIDGET</strong></code>. Paste them below and download the Tawk iOS/Android app to reply to messages on the go.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps mb-2 block">Property ID</Label>
                  <Input data-testid="brand-tawk-property-input" value={form.tawk_property_id || ""} onChange={e => setForm({...form, tawk_property_id: e.target.value})} placeholder="e.g., 65f1c2b8bf24a95..." />
                </div>
                <div>
                  <Label className="label-caps mb-2 block">Widget ID</Label>
                  <Input data-testid="brand-tawk-widget-input" value={form.tawk_widget_id || ""} onChange={e => setForm({...form, tawk_widget_id: e.target.value})} placeholder="e.g., 1hovpuq0i" />
                </div>
              </div>
            </div>
            <HslPicker testid="primary-color" label="Primary color" value={form.primary_color} onChange={v => setForm({...form, primary_color: v})} />
            <HslPicker testid="accent-color" label="Accent color" value={form.accent_color} onChange={v => setForm({...form, accent_color: v})} />
            <Button data-testid="save-branding-btn" onClick={save} disabled={saving} className="w-full">
              {saving ? "Saving…" : "Save & apply"}
            </Button>
          </Card>

          <Card className="p-6">
            <div className="label-caps mb-3">Live preview</div>
            <div className="border border-border rounded-md p-6 bg-background">
              <div className="flex items-center gap-3 mb-5">
                {form.logo_url ? <img src={form.logo_url} alt="" className="h-10 w-10 object-contain" /> :
                  <div className="h-10 w-10 rounded-md flex items-center justify-center text-primary-foreground font-serif text-lg" style={{background: `hsl(${form.primary_color})`}}>{form.brand_name?.[0] || "T"}</div>}
                <div>
                  <div className="font-serif text-2xl leading-none">{form.brand_name}</div>
                  {form.tagline && <div className="text-xs text-muted-foreground mt-1">{form.tagline}</div>}
                </div>
              </div>
              <div className="space-y-3">
                <button className="w-full px-4 py-2 rounded-md text-white text-sm" style={{background: `hsl(${form.primary_color})`}}>Primary button</button>
                <button className="w-full px-4 py-2 rounded-md text-white text-sm" style={{background: `hsl(${form.accent_color})`}}>Accent button</button>
                <div className="p-3 rounded border-l-4" style={{borderColor: `hsl(${form.accent_color})`}}>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Sample callout</div>
                  <div className="font-serif text-lg mt-1">Chapter completed.</div>
                </div>
                {(form.website || form.phone || form.email || form.facebook || form.address) && (
                  <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground space-y-0.5">
                    {form.website && <div>🌐 {form.website}</div>}
                    {form.phone && <div>☎ {form.phone}</div>}
                    {form.email && <div>✉ {form.email}</div>}
                    {form.facebook && <div>ⓕ {form.facebook}</div>}
                    {form.address && <div>📍 {form.address}</div>}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <section className="mt-12">
          <div className="label-caps mb-3">Instructor profile</div>
          <h2 className="font-serif text-3xl mb-4">How students see your replies</h2>
          <Card className="p-6 max-w-2xl">
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="label-caps mb-2 block flex items-center gap-1"><User size={12} /> Profile photo</Label>
                  <div className="flex items-center gap-3">
                    {profile.profile_photo_path ? (
                      <img src={`${API}/files/${profile.profile_photo_path}`} alt="" className="h-16 w-16 rounded-full object-cover border border-border" data-testid="profile-photo-preview" />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-secondary grid place-items-center text-muted-foreground border border-border"><User size={20} /></div>
                    )}
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="profile-photo-input"
                        onChange={e => uploadImage("profile_photo", e.target.files?.[0])}
                      />
                      <Button asChild size="sm" variant="outline" disabled={uploading === "profile_photo"}>
                        <span><Upload size={12} className="mr-1" /> {uploading === "profile_photo" ? "Uploading…" : "Upload"}</span>
                      </Button>
                    </label>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2">Shown next to your Q&A replies. PNG or JPG · 5MB max.</div>
                </div>
                <div>
                  <Label className="label-caps mb-2 block flex items-center gap-1"><Building2 size={12} /> Business logo</Label>
                  <div className="flex items-center gap-3">
                    {profile.business_logo_path ? (
                      <img src={`${API}/files/${profile.business_logo_path}`} alt="" className="h-16 w-16 rounded-md object-contain border border-border bg-white" data-testid="business-logo-preview" />
                    ) : (
                      <div className="h-16 w-16 rounded-md bg-secondary grid place-items-center text-muted-foreground border border-border"><Building2 size={20} /></div>
                    )}
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="business-logo-input"
                        onChange={e => uploadImage("business_logo", e.target.files?.[0])}
                      />
                      <Button asChild size="sm" variant="outline" disabled={uploading === "business_logo"}>
                        <span><Upload size={12} className="mr-1" /> {uploading === "business_logo" ? "Uploading…" : "Upload"}</span>
                      </Button>
                    </label>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2">Your personal business mark for reply signatures.</div>
                </div>
              </div>

              <div>
                <Label className="label-caps mb-2 block">Display name</Label>
                <Input
                  data-testid="instructor-display-name"
                  value={profile.display_name || ""}
                  onChange={e => setProfile({...profile, display_name: e.target.value})}
                  placeholder="Ms. Rivera"
                  maxLength={60}
                />
                <div className="text-[10px] text-muted-foreground mt-1">Shown above your reply on the student's screen.</div>
              </div>
              <div>
                <Label className="label-caps mb-2 block">Signature</Label>
                <Textarea
                  data-testid="instructor-signature"
                  value={profile.signature || ""}
                  onChange={e => setProfile({...profile, signature: e.target.value})}
                  placeholder={"— Ms. Rivera, IRS Enrolled Agent · DyNamic Tax School"}
                  rows={3}
                  maxLength={500}
                />
                <div className="text-[10px] text-muted-foreground mt-1">Appended to every reply you send.</div>
              </div>
              <Button data-testid="save-instructor-profile-btn" onClick={saveProfile} disabled={profileSaving}>
                {profileSaving ? "Saving…" : "Save instructor profile"}
              </Button>
            </div>
            {(profile.display_name || profile.signature || profile.profile_photo_path || profile.business_logo_path) && (
              <div className="mt-6 border-l-4 border-primary pl-4 py-2 bg-secondary/40 rounded-r-md">
                <div className="flex items-center gap-3 mb-2">
                  {profile.profile_photo_path && <img src={`${API}/files/${profile.profile_photo_path}`} alt="" className="h-8 w-8 rounded-full object-cover" />}
                  <div className="label-caps">Preview · Reply · {profile.display_name || "Administrator"}</div>
                </div>
                <div className="text-sm">Here's how your next answer will appear to a student.</div>
                {profile.signature && <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap italic">{profile.signature}</div>}
                {profile.business_logo_path && <img src={`${API}/files/${profile.business_logo_path}`} alt="" className="h-8 mt-2 opacity-80" />}
              </div>
            )}
          </Card>
        </section>

        <section className="mt-12">
          <div className="label-caps mb-3">Instructor team</div>
          <h2 className="font-serif text-3xl mb-4">Add instructors</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">Bring on more teachers to answer student questions. They can sign in from the Instructor tab on the login page.</p>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="label-caps mb-3">Invite a new instructor</div>
              <div className="space-y-3">
                <div>
                  <Label className="label-caps text-[10px] mb-1 block">Full name</Label>
                  <Input data-testid="new-instructor-name" value={newInstructor.name} onChange={e => setNewInstructor({...newInstructor, name: e.target.value})} placeholder="Alicia Ramos" />
                </div>
                <div>
                  <Label className="label-caps text-[10px] mb-1 block">Display name (optional)</Label>
                  <Input data-testid="new-instructor-display-name" value={newInstructor.display_name} onChange={e => setNewInstructor({...newInstructor, display_name: e.target.value})} placeholder="Ms. Ramos" />
                </div>
                <div>
                  <Label className="label-caps text-[10px] mb-1 block">Email</Label>
                  <Input data-testid="new-instructor-email" type="email" value={newInstructor.email} onChange={e => setNewInstructor({...newInstructor, email: e.target.value})} placeholder="alicia@school.com" />
                </div>
                <div>
                  <Label className="label-caps text-[10px] mb-1 block">Temporary password (they can change it after signing in)</Label>
                  <Input data-testid="new-instructor-password" type="text" value={newInstructor.password} onChange={e => setNewInstructor({...newInstructor, password: e.target.value})} placeholder="At least 8 characters" />
                </div>
                <Button onClick={createInstructor} disabled={creating} data-testid="create-instructor-btn" className="w-full">
                  {creating ? "Adding…" : "Add instructor"}
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <div className="label-caps mb-3">Current team ({instructors.length})</div>
              <div className="space-y-2" data-testid="instructor-list">
                {instructors.map(i => (
                  <div key={i.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`instructor-row-${i.id}`}>
                    {i.profile_photo_path ? (
                      <img src={`${API}/files/${i.profile_photo_path}`} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" data-testid={`instructor-photo-${i.id}`} />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-secondary grid place-items-center text-muted-foreground shrink-0 font-serif">{(i.display_name || i.name)?.[0]}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg truncate">
                        {i.display_name || i.name}
                        {i.id === currentUser?.id && <span className="ml-2 label-caps text-[10px] text-accent">You</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{i.email}</div>
                    </div>
                    {i.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeInstructor(i.id, i.name)}
                        data-testid={`remove-instructor-${i.id}`}
                        aria-label="Remove instructor"
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        <section className="mt-12">
          <div className="label-caps mb-3">Trial analytics</div>
          <h2 className="font-serif text-3xl mb-4">Instructor trial funnel</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">
            Track how the 2-day trial is converting students to instructor plans.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl" data-testid="trial-stats-card">
            <Card className="p-5">
              <div className="label-caps text-[10px]">Started</div>
              <div className="font-serif text-4xl mt-1" data-testid="trial-stat-started">{trialStats?.started ?? "—"}</div>
            </Card>
            <Card className="p-5 border-accent/40">
              <div className="label-caps text-[10px] text-accent">Active now</div>
              <div className="font-serif text-4xl mt-1" data-testid="trial-stat-active">{trialStats?.active ?? "—"}</div>
            </Card>
            <Card className="p-5 border-primary/40">
              <div className="label-caps text-[10px] text-primary">Converted</div>
              <div className="font-serif text-4xl mt-1" data-testid="trial-stat-converted">{trialStats?.converted ?? "—"}</div>
              {trialStats && trialStats.started > 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">{trialStats.conversion_pct}% rate</div>
              )}
            </Card>
            <Card className="p-5">
              <div className="label-caps text-[10px]">Expired unpaid</div>
              <div className="font-serif text-4xl mt-1" data-testid="trial-stat-expired">{trialStats?.expired_unpaid ?? "—"}</div>
            </Card>
          </div>
        </section>

        <section className="mt-12">
          <div className="label-caps mb-3">Student welcome</div>
          <h2 className="font-serif text-3xl mb-4">Instructor welcome video</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">
            Record a short 60-second video on your phone or laptop introducing yourself. New students see it on their first dashboard visit — a face makes the course feel personal. Upload MP4, WebM, or MOV (200 MB max).
          </p>
          <Card className="p-5 max-w-xl" data-testid="welcome-video-card">
            {welcome?.video_url ? (
              <>
                <video src={`${BACKEND_URL}${welcome.video_url}`} controls preload="metadata" className="w-full rounded border border-border bg-black aspect-video mb-3" data-testid="welcome-video-preview" />
                <div className="flex gap-2">
                  <label className="inline-flex flex-1">
                    <input type="file" accept="video/*" className="hidden" data-testid="welcome-video-replace" onChange={e => uploadWelcomeVideo(e.target.files?.[0])} />
                    <Button asChild size="sm" variant="outline" className="w-full" disabled={uploadingWelcome}>
                      <span><Upload size={12} className="mr-1" /> {uploadingWelcome ? "Uploading…" : "Replace video"}</span>
                    </Button>
                  </label>
                  <Button size="sm" variant="ghost" onClick={removeWelcomeVideo} data-testid="welcome-video-remove"><Trash2 size={12} /></Button>
                </div>
              </>
            ) : (
              <label className="inline-flex w-full">
                <input type="file" accept="video/*" className="hidden" data-testid="welcome-video-upload" onChange={e => uploadWelcomeVideo(e.target.files?.[0])} />
                <Button asChild size="sm" className="w-full" disabled={uploadingWelcome}>
                  <span><Upload size={12} className="mr-1" /> {uploadingWelcome ? "Uploading…" : "Upload welcome video"}</span>
                </Button>
              </label>
            )}
          </Card>
        </section>

        <section className="mt-12">
          <div className="label-caps mb-3">Email delivery</div>
          <h2 className="font-serif text-3xl mb-4">Password reset emails</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">
            When Resend is configured, forgot-password requests email students a reset link automatically. Otherwise they land in the manual queue.
          </p>
          <Card className="p-6 max-w-2xl" data-testid="email-delivery-card">
            {emailStatus ? (
              <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-start gap-3">
                  {emailStatus.configured ? (
                    <CheckCircle2 size={18} className="text-primary mt-0.5 shrink-0" />
                  ) : (
                    <XCircle size={18} className="text-destructive mt-0.5 shrink-0" />
                  )}
                  <div>
                    <div className="font-medium" data-testid="email-status-label">
                      {emailStatus.configured ? "Resend is configured" : "Resend is not configured"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Sender: <span className="font-mono">{emailStatus.sender}</span>{emailStatus.domain ? ` · Domain: ${emailStatus.domain}` : ""}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground mb-5">Checking status…</div>
            )}

            {!emailStatus?.configured && (
              <div className="text-sm border-l-2 border-accent pl-4 py-3 mb-5 bg-accent/5">
                <div className="label-caps text-accent mb-2">How to turn on real emails (IONOS DNS)</div>
                <ol className="space-y-2 list-decimal ml-5 text-muted-foreground">
                  <li>Sign in at <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a> → <strong>Domains → Add Domain</strong> → enter your domain (e.g. <code>dynamicfinancialsws.online</code>). Resend now shows you 3–4 DNS records.</li>
                  <li>
                    Open <a href="https://www.ionos.com/domains-and-hosting" target="_blank" rel="noreferrer" className="underline">IONOS control panel</a> → <strong>Domains &amp; SSL → your domain → DNS</strong>.
                    For each record Resend gave you, click <strong>Add Record</strong> in IONOS and match the fields:
                    <ul className="list-disc ml-5 mt-1 space-y-1 text-xs">
                      <li><strong>MX record</strong> — Type: <code>MX</code>, Host name: <code>send</code> (or as shown), Points to: <code>feedback-smtp.us-east-1.amazonses.com</code>, Priority: <code>10</code>, TTL: 3600.</li>
                      <li><strong>SPF (TXT)</strong> — Type: <code>TXT</code>, Host name: <code>send</code>, Value: <code>"v=spf1 include:amazonses.com ~all"</code>.</li>
                      <li><strong>DKIM (TXT)</strong> — Type: <code>TXT</code>, Host name: the long <code>resend._domainkey</code>-style value Resend gives you, Value: paste the DKIM string exactly. If IONOS complains about length, split it at the space between the two quoted strings.</li>
                      <li><strong>DMARC (TXT, optional but recommended)</strong> — Type: <code>TXT</code>, Host: <code>_dmarc</code>, Value: <code>"v=DMARC1; p=none;"</code>.</li>
                    </ul>
                    <div className="mt-2 text-xs">IONOS quirk: enter the host as the sub-part only (e.g. <code>send</code>), NOT the full <code>send.dynamicfinancialsws.online</code>.</div>
                  </li>
                  <li>Wait 5–30 min for DNS to propagate. Back in Resend hit <strong>Verify DNS records</strong> until status flips to <strong>Verified</strong>.</li>
                  <li>In Resend go to <strong>API Keys → Create API Key</strong>. Copy the key that starts with <code>re_</code>.</li>
                  <li>
                    Paste it into <code className="text-[11px]">/app/backend/.env</code>:
                    <pre className="text-[11px] bg-secondary rounded p-2 mt-1 overflow-x-auto">RESEND_API_KEY=re_your_actual_key{"\n"}SENDER_EMAIL=noreply@dynamicfinancialsws.online</pre>
                    Then restart the backend and refresh this page.
                  </li>
                </ol>
              </div>
            )}

            <Label className="label-caps mb-2 block">Send a test email</Label>
            <div className="flex flex-wrap gap-2">
              <Input
                data-testid="test-email-to"
                type="email"
                value={testEmailTo}
                onChange={e => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 min-w-[220px]"
                disabled={!emailStatus?.configured}
              />
              <Button
                data-testid="send-test-email-btn"
                onClick={sendTestEmail}
                disabled={!emailStatus?.configured || sendingTest || !testEmailTo}
              >
                <Send size={14} className="mr-2" />
                {sendingTest ? "Sending…" : "Send test"}
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
              <Mail size={11} /> Until your domain is verified, Resend only delivers to the email your Resend account is signed up with.
            </div>
          </Card>
        </section>

        <section className="mt-12">
          <div className="label-caps mb-3">Chapter intros</div>
          <h2 className="font-serif text-3xl mb-4">Chapter intro videos</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">Upload a short intro (MP4/WebM/MOV, up to 200MB) for each chapter. Students see it above the reading. Free chapters 1–2 are the best places to hook new learners.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {chapterVideos.map(c => (
              <Card key={c.chapter_id} className="p-5" data-testid={`chapter-video-row-${c.chapter_id}`}>
                <div className="label-caps mb-1">Ch. {c.chapter_id} · {c.category}</div>
                <div className="font-serif text-xl mb-3">{c.title}</div>
                {c.video_url ? (
                  <>
                    <video src={`${process.env.REACT_APP_BACKEND_URL}${c.video_url}`} controls preload="metadata" className="w-full rounded border border-border bg-black aspect-video mb-2" data-testid={`chapter-video-preview-${c.chapter_id}`} />
                    <div className="flex gap-2">
                      <label className="inline-flex flex-1">
                        <input type="file" accept="video/*" className="hidden" data-testid={`chapter-video-replace-${c.chapter_id}`} onChange={e => uploadChapterVideo(c.chapter_id, e.target.files?.[0])} />
                        <Button asChild size="sm" variant="outline" className="w-full" disabled={uploadingChapter === c.chapter_id}>
                          <span><Upload size={12} className="mr-1" /> {uploadingChapter === c.chapter_id ? "Uploading…" : "Replace"}</span>
                        </Button>
                      </label>
                      <Button size="sm" variant="ghost" onClick={() => removeChapterVideo(c.chapter_id)} data-testid={`chapter-video-remove-${c.chapter_id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </>
                ) : (
                  <label className="inline-flex w-full">
                    <input type="file" accept="video/*" className="hidden" data-testid={`chapter-video-upload-${c.chapter_id}`} onChange={e => uploadChapterVideo(c.chapter_id, e.target.files?.[0])} />
                    <Button asChild size="sm" className="w-full" disabled={uploadingChapter === c.chapter_id}>
                      <span><Upload size={12} className="mr-1" /> {uploadingChapter === c.chapter_id ? "Uploading…" : "Upload video"}</span>
                    </Button>
                  </label>
                )}
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-serif text-3xl mb-4">Case study videos</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">Paste a YouTube, Vimeo, or Loom URL for each case. Students see the video above the interactive 1040 form. Leave blank to hide.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {cases.map(c => (
              <Card key={c.id} className="p-5" data-testid={`admin-video-${c.id}`}>
                <div className="label-caps mb-1">{c.id.replace("_", " ")}</div>
                <div className="font-serif text-xl mb-3">{c.title}</div>
                <Label className="text-xs">Video URL</Label>
                <Input
                  data-testid={`admin-video-input-${c.id}`}
                  className="mt-1 font-mono text-xs"
                  value={videoDrafts[c.id] || ""}
                  onChange={e => setVideoDrafts(v => ({ ...v, [c.id]: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <Button size="sm" className="mt-3" onClick={() => saveVideo(c.id)} data-testid={`admin-video-save-${c.id}`}>Save video</Button>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-12" data-testid="admin-faq-section">
          <div className="label-caps mb-3">Trainable FAQ</div>
          <h2 className="font-serif text-3xl mb-2">Teach your AI helper</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">
            Anything you enter here is fed to your AI helper as a top-priority source. When a visitor asks a question that matches, the helper will answer using your exact wording — not its own paraphrase. Great for pricing exceptions, seasonal promos, or policies you want stated a specific way.
          </p>

          <Card className="p-6 mb-6" data-testid="admin-faq-new">
            <div className="label-caps mb-3">Add a new FAQ</div>
            <div className="space-y-3">
              <div>
                <Label className="label-caps mb-2 block">Question (or trigger phrase)</Label>
                <Input
                  data-testid="admin-faq-new-question"
                  value={faqDraft.question}
                  onChange={e => setFaqDraft({ ...faqDraft, question: e.target.value })}
                  placeholder="How much is the cash-advance fee?"
                />
              </div>
              <div>
                <Label className="label-caps mb-2 block">Answer (used verbatim)</Label>
                <Textarea
                  data-testid="admin-faq-new-answer"
                  value={faqDraft.answer}
                  onChange={e => setFaqDraft({ ...faqDraft, answer: e.target.value })}
                  placeholder="The Refund Advantage cash advance has a $75 fee, taken from your refund — no upfront cost."
                  rows={4}
                />
              </div>
              <Button
                data-testid="admin-faq-new-save"
                onClick={createFaq}
                disabled={faqSaving}
              >
                {faqSaving ? "Saving…" : "Add FAQ"}
              </Button>
            </div>
          </Card>

          <div className="space-y-3">
            <div className="label-caps text-muted-foreground">Existing FAQs ({faqs.length})</div>
            {faqs.length === 0 && (
              <Card className="p-6 text-sm text-muted-foreground" data-testid="admin-faq-empty">
                No FAQs yet. Add your first one above — the AI helper will start using it immediately.
              </Card>
            )}
            {faqs.map(f => (
              <FaqRow
                key={f.id}
                faq={f}
                onSave={patch => updateFaq(f.id, patch)}
                onDelete={() => deleteFaq(f.id)}
              />
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function FaqRow({ faq, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(faq.question);
  const [a, setA] = useState(faq.answer);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!q.trim() || !a.trim()) return;
    setSaving(true);
    await onSave({ question: q.trim(), answer: a.trim() });
    setSaving(false);
    setEditing(false);
  };
  return (
    <Card className="p-5" data-testid={`admin-faq-row-${faq.id}`}>
      {editing ? (
        <div className="space-y-3">
          <Input value={q} onChange={e => setQ(e.target.value)} data-testid={`admin-faq-edit-q-${faq.id}`} />
          <Textarea value={a} onChange={e => setA(e.target.value)} rows={3} data-testid={`admin-faq-edit-a-${faq.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`admin-faq-edit-save-${faq.id}`}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setQ(faq.question); setA(faq.answer); }} data-testid={`admin-faq-edit-cancel-${faq.id}`}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="label-caps text-primary text-[10px]">Question</div>
          <div className="font-medium">{faq.question}</div>
          <div className="label-caps text-primary text-[10px] mt-3">Answer (used verbatim)</div>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap">{faq.answer}</div>
          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`admin-faq-edit-btn-${faq.id}`}>Edit</Button>
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onDelete} data-testid={`admin-faq-delete-btn-${faq.id}`}>
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
